import { PACK_ATTRS } from '@modules/global-style';
import type {
  Preset,
  PackType,
  PresetOption,
  PresetOptionText,
  PresetOptionBorder,
  PresetOptionColor,
  PresetOptionCorner,
  PresetOptionSpacing,
  PresetOptionButton,
} from '@modules/global-style';
import { app } from '@/store';
import cloneDeep from 'lodash.clonedeep';

const SEPARATOR = '';
let textBaseSize = 16;

const formatCssStyle = (cssKey: string, cssValue: string): string => {
  return `${cssKey}: ${cssValue} !important;`;
};

const formatCssClass = (className: string, cssContent: string): string => {
  return `.${className}{${cssContent}}`;
};

const convertJsonToCss = (mappingAttrs: any, packObject: PackType | Partial<PackType>): any => {
  const cssLines = [];

  for (const key in packObject) {
    const cssKey = mappingAttrs[key];
    let cssValue = Object(packObject)[key];

    if (!cssKey || !cssValue || key === 'screens') {
      continue;
    }

    // for font size in text pack only
    if (key === 'fontSize' && cssValue.indexOf('rem') >= 0) {
      cssValue = parseFloat(cssValue) * textBaseSize + 'px';
    }

    cssLines.push(formatCssStyle(cssKey, cssValue));
  }

  return cssLines;
};

const classNameButtonIsDefault = (className: string): boolean => {
  switch (className) {
    case 'gf_gs-button-element':
    case 'gf_gs-button-cart-button':
    case 'gf_gs-button-view-more':
    case 'gf_gs-button-submit':
      return true;
  }
  return false;
};

export const generateClassName = (presetOption: PresetOption) => {
  const subfix = presetOption.key || presetOption.id;

  return `gf_gs-${presetOption.type}-${subfix}`;
};

export const getPackAttrByKey = (key: any, presetOption: PresetOptionText) => {
  const mappingAttrs: any = cloneDeep((PACK_ATTRS as any)[key]);
  if (presetOption?.subType == 'heading') {
    delete mappingAttrs?.paragraphFont;
  } else if (presetOption?.subType == 'paragraph') {
    delete mappingAttrs?.headingFont;
  }
  return mappingAttrs;
};

const convertTextOptionToCssObject = (presetOption: PresetOptionText): string => {
  const mappingAttrs: any = getPackAttrByKey(presetOption.type, presetOption);
  const textValue = presetOption.value;
  const className = generateClassName(presetOption);
  const cssObject = Object({});

  let cssLines = [],
    cssLgLines = [],
    cssMdLines = [],
    cssSmLines = [],
    cssXsLines = [];

  textBaseSize = parseInt(textValue.baseSize);
  cssLines = convertJsonToCss(mappingAttrs, textValue);

  if (textValue.screens) {
    const screensValue = textValue.screens;

    cssLgLines = convertJsonToCss(mappingAttrs, screensValue.lg);
    cssMdLines = convertJsonToCss(mappingAttrs, screensValue.md);
    cssSmLines = convertJsonToCss(mappingAttrs, screensValue.sm);
    cssXsLines = convertJsonToCss(mappingAttrs, screensValue.xs);
  }

  cssObject[className] = cssLines.join(SEPARATOR);
  cssObject[className + ':xs'] = cssXsLines.join(SEPARATOR);
  cssObject[className + ':sm'] = cssSmLines.join(SEPARATOR);
  cssObject[className + ':md'] = cssMdLines.join(SEPARATOR);
  cssObject[className + ':lg'] = cssLgLines.join(SEPARATOR);

  return cssObject;
};

export const convertBorderOptionToCssObject = (presetOption: PresetOptionBorder): string => {
  const mappingAttrs = PACK_ATTRS[presetOption.type];
  const borderValue = presetOption.value;
  const className = generateClassName(presetOption);
  const cssObject = Object({});

  cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button`] =
    convertJsonToCss(mappingAttrs, borderValue).join(SEPARATOR);

  return cssObject;
};

const convertCornerOptionToCssObject = (presetOption: PresetOptionCorner): string => {
  const cornerValue = presetOption.value;
  const className = generateClassName(presetOption);
  const cssObject = Object({});

  cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button`] =
    formatCssStyle('border-radius', cornerValue.value);

  return cssObject;
};

const convertSpacingOptionToCssObject = (presetOption: PresetOptionSpacing): string => {
  const spacingValue = presetOption.value;
  const signature = presetOption.key || presetOption.id;
  const cssObject = Object({});

  // Generate Padding CSS
  cssObject[`gf_gs-spacing-${signature}-p:not([data-key=dynamic-button])`] = formatCssStyle(
    'padding',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-px:not([data-key=dynamic-button])`] = `${formatCssStyle(
    'padding-left',
    spacingValue.value,
  )}${formatCssStyle('padding-right', spacingValue.value)}`;
  cssObject[`gf_gs-spacing-${signature}-py:not([data-key=dynamic-button])`] = `${formatCssStyle(
    'padding-top',
    spacingValue.value,
  )}${formatCssStyle('padding-bottom', spacingValue.value)}`;
  cssObject[`gf_gs-spacing-${signature}-p-top:not([data-key=dynamic-button])`] = formatCssStyle(
    'padding-top',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-p-right:not([data-key=dynamic-button])`] = formatCssStyle(
    'padding-right',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-p-bottom:not([data-key=dynamic-button])`] = formatCssStyle(
    'padding-bottom',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-p-left:not([data-key=dynamic-button])`] = formatCssStyle(
    'padding-left',
    spacingValue.value,
  );

  // Support Dynamic Button
  cssObject[`gf_gs-spacing-${signature}-p .shopify-payment-button__button`] = formatCssStyle(
    'padding',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-px .shopify-payment-button__button`] = `${formatCssStyle(
    'padding-left',
    spacingValue.value,
  )}${formatCssStyle('padding-right', spacingValue.value)}`;
  cssObject[`gf_gs-spacing-${signature}-py .shopify-payment-button__button`] = `${formatCssStyle(
    'padding-top',
    spacingValue.value,
  )}${formatCssStyle('padding-bottom', spacingValue.value)}`;
  cssObject[`gf_gs-spacing-${signature}-p-top .shopify-payment-button__button`] = formatCssStyle(
    'padding-top',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-p-right .shopify-payment-button__button`] = formatCssStyle(
    'padding-right',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-p-bottom .shopify-payment-button__button`] = formatCssStyle(
    'padding-bottom',
    spacingValue.value,
  );
  cssObject[`gf_gs-spacing-${signature}-p-left .shopify-payment-button__button`] = formatCssStyle(
    'padding-left',
    spacingValue.value,
  );

  // Generate Margin CSS
  cssObject[`gf_gs-spacing-${signature}-m`] = formatCssStyle('margin', spacingValue.value);
  cssObject[`gf_gs-spacing-${signature}-mx`] = `${formatCssStyle('margin-left', spacingValue.value)}${formatCssStyle(
    'margin-right',
    spacingValue.value,
  )}`;
  cssObject[`gf_gs-spacing-${signature}-my`] = `${formatCssStyle('margin-top', spacingValue.value)}${formatCssStyle(
    'margin-bottom',
    spacingValue.value,
  )}`;
  cssObject[`gf_gs-spacing-${signature}-m-top`] = formatCssStyle('margin-top', spacingValue.value);
  cssObject[`gf_gs-spacing-${signature}-m-right`] = formatCssStyle('margin-right', spacingValue.value);
  cssObject[`gf_gs-spacing-${signature}-m-bottom`] = formatCssStyle('margin-bottom', spacingValue.value);
  cssObject[`gf_gs-spacing-${signature}-m-left`] = formatCssStyle('margin-left', spacingValue.value);
  return cssObject;
};

export const convertColorOptionToCssObject = (presetOption: PresetOptionColor): string => {
  const colorValue = presetOption.value;
  const cssObject = Object({});

  for (const colorType in colorValue) {
    for (const color in Object(colorValue)[colorType]) {
      const className = `gf_gs-${color}-${colorType}`; // gf_gs-color1-neutral

      cssObject[`${className}:root`] = `--${className}: ${Object(colorValue)[colorType][color]};`;
    }
  }

  cssObject['gf_gs-black-default:root'] = '--gf_gs-black-default: #000000ff;';
  cssObject['gf_gs-white-default:root'] = '--gf_gs-white-default: #ffffffff;';
  cssObject['gf_gs-transparent-default:root'] = '--gf_gs-transparent-default: #ffffff00;';

  return cssObject;
};

const convertButtonSizeOptionToCssObject = (
  presetOption: PresetOptionText,
  classButtonName: string,
  size: string,
): string => {
  const className = `${classButtonName}.gf_gs-button---${size}`;
  const mappingAttrs: any = getPackAttrByKey('textButtonSize', presetOption);

  const textValue = presetOption.value;
  const cssObject = Object({});

  let cssLines = [],
    cssLgLines = [],
    cssMdLines = [],
    cssSmLines = [],
    cssXsLines = [];
  textBaseSize = parseInt(textValue.baseSize);
  cssLines = convertJsonToCss(mappingAttrs, textValue);
  if (textValue.screens) {
    const screensValue = textValue.screens;

    cssLgLines = convertJsonToCss(mappingAttrs, screensValue.lg);
    cssMdLines = convertJsonToCss(mappingAttrs, screensValue.md);
    cssSmLines = convertJsonToCss(mappingAttrs, screensValue.sm);
    cssXsLines = convertJsonToCss(mappingAttrs, screensValue.xs);
  }
  if (classNameButtonIsDefault(classButtonName)) {
    cssObject[className] = cssLines.join(SEPARATOR);
    cssObject[`${className}:xs`] = cssXsLines.join(SEPARATOR);
    cssObject[`${className}:sm`] = cssSmLines.join(SEPARATOR);
    cssObject[`${className}:md`] = cssMdLines.join(SEPARATOR);
    cssObject[`${className}:lg`] = cssLgLines.join(SEPARATOR);
  } else {
    cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button`] =
      cssLines.join(SEPARATOR);
    cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button:xs`] =
      cssXsLines.join(SEPARATOR);
    cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button:sm`] =
      cssSmLines.join(SEPARATOR);
    cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button:md`] =
      cssMdLines.join(SEPARATOR);
    cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button:lg`] =
      cssLgLines.join(SEPARATOR);
  }
  return cssObject;
};

export const convertButtonOptionToCssObject = (
  presetOption: PresetOptionButton,
  presetTexts: PresetOptionText[],
  preset: Preset,
): any => {
  const cssLines = [];
  const cssHoverLines = [];
  const buttonValue = presetOption.value;
  const className = generateClassName(presetOption);

  let cssObject = Object({});
  if (!preset) {
    preset = (app as any).$store.state.sidebar.currentPresetInPage;
    const previewMode = (app as any).$store.state.sidebar.previewPreset;
    if (previewMode) {
      preset = (app as any).$store.state.sidebar.currentPreset;
    }
  }

  if (buttonValue.dropShadow && buttonValue.dropShadow.enabled) {
    const radian = parseInt(buttonValue.dropShadow.angle) * (Math.PI / 180);
    const x = Math.cos(radian) * parseFloat(buttonValue.dropShadow.distance);
    const y = Math.sin(radian) * parseFloat(buttonValue.dropShadow.distance);
    const xPx = `${x ? x : 0}px`;
    const yPx = `${y ? y : 0}px`;
    const boxShadowValue = `${xPx} ${yPx} ${buttonValue.dropShadow.blur} ${buttonValue.dropShadow.size} ${buttonValue.dropShadow.color}`;
    cssLines.push(formatCssStyle('box-shadow', boxShadowValue));
  } else {
    cssLines.push(formatCssStyle('box-shadow', '0 0'));
  }

  if (buttonValue.backgroundColor) {
    cssLines.push(formatCssStyle('background-color', buttonValue.backgroundColor.origin));
    cssHoverLines.push(formatCssStyle('background-color', buttonValue.backgroundColor.hover));
  }

  if (buttonValue.textColor) {
    cssLines.push(formatCssStyle('color', buttonValue.textColor.origin));
    cssHoverLines.push(formatCssStyle('color', buttonValue.textColor.hover));
  }

  if (buttonValue.cornerStyle) {
    const presetConners: any = preset?.presetOptions?.corners || [];
    const corner = presetConners.find((item: { id: any }) => item.id == buttonValue.cornerStyle);
    if (corner) {
      cssLines.push(formatCssStyle('border-radius', corner.value.value));
    }
  } else {
    // if corner == none set value = 0
    cssLines.push(formatCssStyle('border-radius', '0'));
  }

  if (buttonValue.borderStyle) {
    const presetBorders: any = preset?.presetOptions?.borders || [];
    const border = presetBorders.find((item: { id: any }) => item.id == buttonValue.borderStyle);
    if (border) {
      const borderValue = border.value;
      cssLines.push(formatCssStyle('border-color', borderValue.color));
      cssLines.push(formatCssStyle('border-width', borderValue.thickness));
      cssLines.push(formatCssStyle('border-style', borderValue.style));
    }
  } else {
    cssLines.push(formatCssStyle('border', 'none'));
  }

  const textStyle = buttonValue.textStyle;
  if (textStyle) {
    const keyTxtLarge = textStyle.large;
    const keyTxtMedium = textStyle.medium;
    const keyTxtSmall = textStyle.small;
    if (keyTxtLarge) {
      const text: any = presetTexts.find((item: any) => item.key == keyTxtLarge);
      if (text && Object.keys(text).length > 0) {
        const cssObjectButtonLarge: any = convertButtonSizeOptionToCssObject(text, className, 'large');
        cssObject = {
          ...cssObject,
          ...cssObjectButtonLarge,
        };
      }
    }
    if (keyTxtMedium) {
      const text: any = presetTexts.find((item: any) => item.key == keyTxtMedium);
      if (text && Object.keys(text).length > 0) {
        const cssObjectButtonMedium: any = convertButtonSizeOptionToCssObject(text, className, 'medium');
        cssObject = {
          ...cssObject,
          ...cssObjectButtonMedium,
        };
      }
    }
    if (keyTxtSmall) {
      const text: any = presetTexts.find((item: any) => item.key == keyTxtSmall);
      if (text && Object.keys(text).length > 0) {
        const cssObjectButtonSmall: any = convertButtonSizeOptionToCssObject(text, className, 'small');
        cssObject = {
          ...cssObject,
          ...cssObjectButtonSmall,
        };
      }
    }
  }
  if (classNameButtonIsDefault(className)) {
    cssObject[className] = cssLines.join(SEPARATOR);
    cssObject[`${className}:hover`] = cssHoverLines.join(SEPARATOR);
  } else {
    cssObject[`${className}:not([data-key=dynamic-button]), .${className} .shopify-payment-button__button`] =
      cssLines.join(SEPARATOR);
    cssObject[
      `${className}:not([data-key=dynamic-button]):hover, .${className} .shopify-payment-button__button:hover`
    ] = cssHoverLines.join(SEPARATOR);
  }
  return cssObject;
};

export const generateCssObject = (preset: Preset): any => {
  if (!preset) return;

  const presetOptions = Object(preset.presetOptions);
  const cssObject = Object({});

  for (const key in presetOptions) {
    const listPresetOption = presetOptions[key];

    if (key === 'borders') {
      listPresetOption.forEach((presetOption: PresetOptionBorder) => {
        Object.assign(cssObject, convertBorderOptionToCssObject(presetOption));
      });
    } else if (key === 'corners') {
      listPresetOption.forEach((presetOption: PresetOptionCorner) => {
        Object.assign(cssObject, convertCornerOptionToCssObject(presetOption));
      });
    } else if (key === 'colors') {
      listPresetOption.forEach((presetOption: PresetOptionColor) => {
        Object.assign(cssObject, convertColorOptionToCssObject(presetOption));
      });
    } else if (key === 'spacings') {
      listPresetOption.forEach((presetOption: PresetOptionSpacing) => {
        Object.assign(cssObject, convertSpacingOptionToCssObject(presetOption));
      });
    } else if (key === 'buttons') {
      listPresetOption.forEach((presetOption: PresetOptionButton) => {
        Object.assign(cssObject, convertButtonOptionToCssObject(presetOption, presetOptions['texts'], preset));
      });
    } else {
      listPresetOption.forEach((presetOption: PresetOptionText) => {
        Object.assign(cssObject, convertTextOptionToCssObject(presetOption));
      });
    }
  }

  return cssObject;
};

export const generateCssString = (preset: Preset): any => {
  const cssObject = generateCssObject(preset);
  const cssElements = [];
  const cssRootElements = [];
  const mediaCssElements = Object({
    lg: [],
    md: [],
    sm: [],
    xs: [],
  });

  for (const key in cssObject) {
    const cssElement = cssObject[key];
    const partern = /(.*):(lg|md|sm|xs)$/;
    const rootPartern = /(.*):root$/;
    const resultMatched = key.match(partern);
    const resultRootMatched = key.match(rootPartern);

    if (resultMatched) {
      const mediaType = resultMatched[2];
      const className = resultMatched[1];

      mediaCssElements[mediaType].push(formatCssClass(className, cssElement));
    } else if (resultRootMatched) {
      cssRootElements.push(cssElement);
    } else {
      cssElements.push(formatCssClass(key, cssElement));
    }
  }

  const mediaLgString = `@media (min-width: 1200px){${mediaCssElements.lg.join(SEPARATOR)}}`;
  const mediaMdString = `@media (min-width: 992px) and (max-width: 1199px){${mediaCssElements.md.join(SEPARATOR)}}`;
  const mediaSmString = `@media (min-width: 768px) and (max-width: 991px) {${mediaCssElements.sm.join(SEPARATOR)}}`;
  const mediaXsString = `@media (max-width: 767px){${mediaCssElements.xs.join(SEPARATOR)}}`;
  const rootCssString = `:root{${cssRootElements.join(SEPARATOR)}}`;

  return rootCssString + cssElements.join(SEPARATOR) + mediaLgString + mediaMdString + mediaSmString + mediaXsString;
};

export const presetCheckValueChanged = (oldPreset: Preset, newPreset: Preset): boolean => {
  if (oldPreset.id !== newPreset.id) {
    return true;
  }
  const oldPresetOptions = Object(cloneDeep(oldPreset.presetOptions));
  const newPresetOptions = Object(cloneDeep(newPreset.presetOptions));

  const oldData: Record<string, any> = {};
  for (const key in oldPresetOptions) {
    const listPresetOption = oldPresetOptions[key];

    if (key === 'texts') {
      const dataObjects: any[] = [];
      listPresetOption.forEach((presetOption: PresetOptionText) => {
        const { screens, scale, ...value } = presetOption.value;
        const dataObject = {
          ...presetOption,
          ...{
            value: {
              ...value,
              fontSize: {
                lg: presetOption.value.screens?.lg?.fontSize,
                sm: presetOption.value.screens?.sm?.fontSize,
              },
              lineHeight: {
                lg: presetOption.value.screens?.lg?.lineHeight,
                sm: presetOption.value.screens?.sm?.lineHeight,
              },
            },
          },
        };
        dataObjects.push(dataObject);
      });
      oldData[key] = dataObjects;
    } else {
      oldData[key] = { ...listPresetOption };
    }
  }

  const newData: Record<string, any> = {};
  for (const key in newPresetOptions) {
    const listPresetOption = newPresetOptions[key];

    if (key === 'texts') {
      const dataObjects: any[] = [];
      listPresetOption.forEach((presetOption: PresetOptionText) => {
        const { screens, scale, ...value } = presetOption.value;
        const dataObject = {
          ...presetOption,
          ...{
            value: {
              ...value,
              fontSize: {
                lg: presetOption.value.screens?.lg?.fontSize,
                sm: presetOption.value.screens?.sm?.fontSize,
              },
              lineHeight: {
                lg: presetOption.value.screens?.lg?.lineHeight,
                sm: presetOption.value.screens?.sm?.lineHeight,
              },
            },
          },
        };
        dataObjects.push(dataObject);
      });
      newData[key] = dataObjects;
    } else {
      newData[key] = { ...listPresetOption };
    }
  }

  return JSON.stringify(oldData) !== JSON.stringify(newData);
};
